export type ProviderCode =
  | 'MOCK'
  | 'APNS'
  | 'FCM'
  | 'JPUSH'
  | 'HUAWEI_PUSH'
  | 'HARMONY_PUSH'
  | 'XIAOMI_PUSH'
  | 'OPPO_PUSH'
  | 'VIVO_PUSH'
  | 'GORUSH'
  | 'WECHAT_MINIAPP_SUBSCRIBE'
  | 'WECHAT_MP_TEMPLATE'

export type AudienceType = 'USER' | 'USER_IDS' | 'DEVICE_IDS' | 'TAGS' | 'ALL' | 'TENANT' | 'ROLE'
export type PushTab =
  'configs' | 'templates' | 'wechat' | 'devices' | 'notifications' | 'tasks' | 'logs' | 'settings'
export type WechatChannelCode = 'WECHAT_MINIAPP_SUBSCRIBE' | 'WECHAT_MP_TEMPLATE'
export type NotificationChannelCode = 'INBOX' | 'APP_PUSH' | WechatChannelCode

export interface PushApp {
  id: number
  tenantId: number
  appCode: string
  appName: string
  bundleId: string
  packageName: string
  harmonyAppId: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export function pushAppOptions(apps: PushApp[]) {
  return [
    { label: '全部应用', value: '' },
    ...apps.map((app) => ({ label: `${app.appName} (${app.appCode})`, value: app.appCode }))
  ]
}

export interface PushChannelConfig {
  id: number
  tenantId: number
  appCode: string
  provider: ProviderCode
  environment: string
  credentialMasked: Record<string, unknown>
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface PushDevice {
  id: number
  userId: number
  userType: string
  appCode: string
  platform: string
  provider: ProviderCode
  tokenMasked: string
  deviceId: string
  installId: string
  appVersion: string
  osVersion: string
  enabled: boolean
  lastSeenAt: string
}

export interface PushTemplate {
  id: number
  tenantId: number
  appCode: string
  templateCode: string
  templateName: string
  titleTemplate: string
  bodyTemplate: string
  dataTemplateJson: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface PushTask {
  id: number
  appCode: string
  notificationId?: number | null
  audienceType: AudienceType
  provider: ProviderCode
  title: string
  body: string
  status: string
  totalCount: number
  successCount: number
  failureCount: number
  skippedCount: number
  createdAt: string
  sendTime?: string | null
}

export interface PushDeliveryView {
  id: number
  taskId?: number | null
  notificationId?: number | null
  clientUserId?: number | null
  provider: ProviderCode
  status: string
  errorCode?: string | null
  createdAt?: string | null
}

export interface PushTaskDetail {
  task: PushTask
  deliveries: PushDeliveryView[]
  logs: PushDeliveryLog[]
}

export interface NotificationDetail {
  notification: NotificationView
  inboxRecords: Array<{ id: number; clientUserId: number; readStatus: number }>
  pushTasks: PushTask[]
  deliveries: PushDeliveryView[]
  deliveryLogs: PushDeliveryLog[]
}

export interface NotificationView {
  id: number
  appCode: string
  moduleCode: string
  productCode: string
  clientUserId?: number | null
  audienceType: AudienceType
  category: string
  title: string
  body: string
  level: string
  route: string
  status: string
  needInbox: boolean
  needPush: boolean
  createTime: string
  inboxCount: number
  unreadCount: number
  taskCount: number
  deliveryCount: number
  deliveredCount: number
  failedCount: number
  ackedCount: number
}

export interface NotificationStats {
  totalNotifications: number
  sentNotifications: number
  revokedNotifications: number
  inboxRecords: number
  unreadInboxRecords: number
  deliveryTotal: number
  deliveryPending: number
  deliverySent: number
  deliveryFailed: number
  deliveryAcked: number
  categoryDistribution: Record<string, number>
  providerDistribution: Record<string, number>
}

export interface PushDeliveryLog {
  id: number
  messageId: number
  taskId: number
  deviceId: number
  provider: ProviderCode
  requestId: string
  providerMessageId: string
  success: boolean
  errorCode: string
  errorMessage: string
  createdAt: string
}

export interface PushSetting {
  userId?: number
  appCode: string
  optIn: boolean
  quietStart: string
  quietEnd: string
  channelSettingsJson: Record<string, unknown>
  updatedAt?: string
}

export interface PushChannelTestResult {
  success: boolean
  message: string
  latencyMs: number
  level: 'VALIDATION' | 'AUTHENTICATION'
}

export type ChannelCategory = 'all' | 'vendor' | 'aggregated' | 'wechat' | 'debug'

export const CHANNEL_CATEGORIES: { key: ChannelCategory; label: string; description: string }[] = [
  { key: 'all', label: '全部通道', description: '所有已集成的推送服务' },
  { key: 'vendor', label: '原生厂商通道', description: '苹果、华为、小米、OPPO、vivo、鸿蒙、FCM' },
  { key: 'aggregated', label: '聚合推送', description: '极光推送、Gorush 自建通道' },
  { key: 'wechat', label: '微信触达', description: '小程序订阅消息、公众号模板消息' },
  { key: 'debug', label: '调试测试', description: '本地联调 Mock 虚拟通道' }
]

export interface ProviderMeta {
  label: string
  platform: string
  accent: string
  category: ChannelCategory
  description: string
}

export const PROVIDER_CATALOG: Record<ProviderCode, ProviderMeta> = {
  MOCK: {
    label: 'Mock 通道',
    platform: '本地/测试',
    accent: 'neutral',
    category: 'debug',
    description: '模拟推送投递与到达，无需外部配置'
  },
  JPUSH: {
    label: '极光推送',
    platform: 'Android/iOS 聚合',
    accent: 'green',
    category: 'aggregated',
    description: '国内常用聚合推送通道，含离线厂商兜底'
  },
  APNS: {
    label: 'Apple APNs',
    platform: 'iOS/iPadOS/macOS',
    accent: 'blue',
    category: 'vendor',
    description: '苹果官方推送通道，需提供 p8 认证私钥'
  },
  FCM: {
    label: 'Google FCM',
    platform: 'Android (海外)',
    accent: 'amber',
    category: 'vendor',
    description: 'Google Firebase 云推送，需 Service Account'
  },
  HUAWEI_PUSH: {
    label: '华为推送',
    platform: 'EMUI / Harmony 兼容',
    accent: 'red',
    category: 'vendor',
    description: '华为 HMS Core 推送服务'
  },
  HARMONY_PUSH: {
    label: '鸿蒙推送',
    platform: 'HarmonyOS NEXT',
    accent: 'red',
    category: 'vendor',
    description: '华为纯血鸿蒙操作系统原生推送'
  },
  XIAOMI_PUSH: {
    label: '小米推送',
    platform: 'HyperOS / MIUI',
    accent: 'orange',
    category: 'vendor',
    description: '小米开发者推送平台，需 AppSecret'
  },
  OPPO_PUSH: {
    label: 'OPPO 推送',
    platform: 'ColorOS',
    accent: 'green',
    category: 'vendor',
    description: 'OPPO 开放平台推送服务，需 AppKey/MasterSecret'
  },
  VIVO_PUSH: {
    label: 'vivo 推送',
    platform: 'OriginOS / FuntouchOS',
    accent: 'blue',
    category: 'vendor',
    description: 'vivo 开放平台推送服务，需 AppID/AppKey/AppSecret'
  },
  GORUSH: {
    label: 'Gorush 通道',
    platform: '自建私有化',
    accent: 'neutral',
    category: 'aggregated',
    description: '基于 Gorush 开源网关的自建推送服务'
  },
  WECHAT_MINIAPP_SUBSCRIBE: {
    label: '微信小程序订阅',
    platform: '微信小程序',
    accent: 'green',
    category: 'wechat',
    description: '微信客户端一次性或长期订阅消息'
  },
  WECHAT_MP_TEMPLATE: {
    label: '微信公众号模板',
    platform: '微信服务号',
    accent: 'green',
    category: 'wechat',
    description: '微信服务号粉丝模板消息触达'
  }
}

export interface ProviderStats {
  provider: ProviderCode
  total: number
  pending: number
  sent: number
  failed: number
  acked: number
  successRate: number
}

export interface WechatChannelTemplate {
  id: number
  tenantId: number
  appCode: string
  eventCode: string
  channelCode: WechatChannelCode
  providerTemplateId: string
  dataMapping?: Record<string, string>
  dataMappingJson?: string | Record<string, string>
  pagePath?: string | null
  miniappState?: string | null
  language?: string | null
  enabled: boolean
  createdAt?: string
  updatedAt?: string
}

export interface WechatUserBinding {
  id: number
  tenantId: number
  clientUserId: number
  appCode: string
  channelType: WechatChannelCode
  appId: string
  openId?: string
  openIdMasked?: string
  unionId?: string | null
  unionIdMasked?: string | null
  followed?: boolean | null
  followStatus?: string | null
  enabled: boolean
  lastSeenAt?: string | null
}

export interface WechatSubscription {
  id: number
  bindingId: number
  clientUserId?: number | null
  appCode?: string | null
  providerTemplateId: string
  authorizationType: 'ONCE' | 'LONG_TERM' | string
  status: string
  remainingCount?: number | null
  authorizedAt?: string | null
  consumedAt?: string | null
}

export const PUSH_PROVIDERS: ProviderCode[] = [
  'JPUSH',
  'APNS',
  'FCM',
  'HUAWEI_PUSH',
  'HARMONY_PUSH',
  'XIAOMI_PUSH',
  'OPPO_PUSH',
  'VIVO_PUSH',
  'GORUSH',
  'WECHAT_MINIAPP_SUBSCRIBE',
  'WECHAT_MP_TEMPLATE'
]

export const AUDIENCE_TYPES: AudienceType[] = ['ALL', 'USER_IDS', 'DEVICE_IDS', 'TAGS', 'TENANT']

export const NOTIFICATION_CHANNELS: {
  code: NotificationChannelCode
  label: string
  detail: string
}[] = [
  { code: 'INBOX', label: '站内信', detail: '写入用户消息中心' },
  { code: 'APP_PUSH', label: 'App Push', detail: '按设备厂商通道投递' },
  { code: 'WECHAT_MINIAPP_SUBSCRIBE', label: '小程序', detail: '订阅消息' },
  { code: 'WECHAT_MP_TEMPLATE', label: '公众号', detail: '模板消息' }
]
